import { type JSX, useEffect, useRef, useState } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { FormLabel } from 'Components/FormField/FormLabel/FormLabel';
import { Text } from 'Components/Text/Text';
import { GenericTooltip } from 'Components/Tooltips/GenericTooltip';
import { ICON_CLOSE, ICON_COPY, ICON_POSITIVE } from 'Config/Icons';
import { AiContentAdvisorValues } from 'Hooks/AiContentAdvisor';
import { AdvancedFeature } from 'Models/ClientDetailsModel';

import styles from './FormFieldTextAreaAi.module.css';

interface FormFieldTextAreaAiProps {
    formFieldId: string;
    formFieldLabel: string;
    required?: boolean;
    hideRequiredSymbol?: boolean;
    tooltip?: string;
    placeholder?: string;
    handleChange?: any;
    disabled?: boolean;
    value?: string;
    rows?: number;

    /** Provided by the `useAiContentAdvisor` hook. */
    aiContentAdvisorValues: AiContentAdvisorValues;
}

/** Wraps the FormFieldTextArea component and adds AI feedback functionality. Feedback remains visible until the user dismisses it. */
export const FormFieldTextAreaAi = (props: FormFieldTextAreaAiProps): JSX.Element => {
    const cachedData = useCachedData();
    const recommendation = props.aiContentAdvisorValues.analysis?.recommendation;
    const [recommendationCopied, setRecommendationCopied] = useState(false);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    // Avoid setting state after unmount.
    useEffect(() => () => clearTimeout(copiedTimeoutRef.current), []);

    const copyRecommendation = async (text: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // Clipboard access can be denied (e.g., in a non-secure context); don't claim success.
            return;
        }
        setRecommendationCopied(true);
        // Repeated clicks restart the 2-second window.
        clearTimeout(copiedTimeoutRef.current);
        copiedTimeoutRef.current = setTimeout(() => setRecommendationCopied(false), 2000);
    };

    const copyLabel = recommendationCopied ? 'Copied!' : 'Copy to clipboard';

    return (
        <div className={styles.container}>
            <div className={styles.labelContainer}>
                <FormLabel htmlFor={props.formFieldId} required={props.required} hideRequiredSymbol={props.hideRequiredSymbol} tooltip={props.tooltip}>
                    {props.formFieldLabel}
                </FormLabel>
                {cachedData.clientHasAdvancedFeature([AdvancedFeature.AI_CONTENT_ADVISOR]) && (
                    <Button variant="linkText" size="sm" onClick={props.aiContentAdvisorValues.onAnalyzeButtonClicked} disabled={props.disabled || props.aiContentAdvisorValues.analysisIsLoading || props.aiContentAdvisorValues.currentContentHasBeenAnalyzed || (props.value?.trim().length ?? 0) === 0}>
                        {props.aiContentAdvisorValues.analysisIsLoading ? 'Analyzing...' : 'Analyze with SummIT Guide™ Content Advisor'}
                    </Button>
                )}
            </div>
            <FormFieldTextArea formFieldId={props.formFieldId} required={props.required} hideRequiredSymbol={props.hideRequiredSymbol} placeholder={props.placeholder} handleChange={props.handleChange} disabled={props.disabled} value={props.value} rows={props.rows} />
            {props.aiContentAdvisorValues.analysis && (
                <div className={styles.feedbackContainer}>
                    <div className={styles.feedbackHeader}>
                        <Text variant="Header4" noStyles>
                            Feedback
                        </Text>
                        <GenericTooltip text="Dismiss feedback" placement="left">
                            <IconButton aria-label="Dismiss feedback" fontAwesomeImage={ICON_CLOSE} onClick={props.aiContentAdvisorValues.onDismissAnalysis} />
                        </GenericTooltip>
                    </div>
                    <Text preserveLineBreaks>{props.aiContentAdvisorValues.analysis.feedback}</Text>
                    {recommendation && (
                        <>
                            <div className={styles.feedbackHeader}>
                                <Text variant="Header4" noStyles>
                                    Recommended Text
                                </Text>
                                <GenericTooltip text={copyLabel} placement="left">
                                    <IconButton aria-label={copyLabel} fontAwesomeImage={recommendationCopied ? ICON_POSITIVE : ICON_COPY} onClick={() => copyRecommendation(recommendation)} />
                                </GenericTooltip>
                            </div>
                            <Text preserveLineBreaks>{recommendation}</Text>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
